import * as Sentry from "@sentry/vue";
import { computed, watch } from "vue";
import { useI18n as _useI18n, createI18n } from "vue-i18n";

import { useCurrentTranslations } from "@/composables/useCurrentTranslations";

interface Translation {
  key: string;
  value: string;
}

/**
 * Escape `@` because Vue uses it as a special character for events like `@click`.
 */
function sanitize(text: string | null | undefined) {
  return !text ? "" : text.replace(/@/g, "{'@'}");
}

// Use Set to prevent duplicate error messages
const reportedMissingKeys = new Set<string>();

export const i18n = createI18n({
  locale: "default",
  fallbackLocale: "default",
  legacy: false,
  warnHtmlMessage: false,
  missingWarn: false,
  missing: (_locale, key) => {
    if (key && !reportedMissingKeys.has(key)) {
      // Prevent duplicate error messages
      reportedMissingKeys.add(key);

      // Throw an error that Sentry can capture
      const error = new Error(`Missing translation: ${key}`);
      error.name = "MissingTranslationError";

      Sentry.captureException(error, {
        tags: { type: "translation_error" },
        extra: { key },
        // Group all missing translation errors together while keeping them unique.
        fingerprint: ["missing-translation-error"],
      });
    }

    return `${key}`;
  },
});

export function useI18n() {
  const { setLocaleMessage, t: originalT } = _useI18n();
  const { data: translations } = useCurrentTranslations();

  const messages = computed(() => {
    const result: { [key: string]: string } = {};

    if (!translations.value) {
      return result;
    }

    for (const translation of translations.value as Translation[]) {
      const { key, value } = translation;

      result[key] = sanitize(value);
    }

    return result;
  });

  watch(
    messages,
    () => {
      setLocaleMessage("default", messages.value);
    },
    { immediate: true },
  );

  const t = (key: string, options?: number | Record<string, unknown>) => {
    if (typeof options === "number") {
      return originalT(key, { count: options });
    }
    return originalT(key, options || {});
  };

  return { t };
}
